// Session PJ2: one survey photo on the office's panel: the thumbnail (streamed through the platform, never a Drive link), a two-step
// delete and, on a data plate or label, "Read this plate". Reading WRITES NOTHING: it proposes values beside what is recorded now,
// and the office accepts each one, which saves it as read from the photo and confirmed by the office.
import { useEffect, useState } from "react";
import { api, ApiError, type ProjectSurveyPhotoView, type ProjectSurveyPlateProposal, type ProjectSurveyPlateRead } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { formatDateTime } from "../../../lib/format";
import { isPlateSlot } from "./surveyOfficeUi";

const quietBtn = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";

export function SurveyPhotoItem({
  surveyId,
  photo,
  slotLabel,
  describeCurrent,
  canAccept,
  onAccept,
  onDeleted,
}: {
  surveyId: string;
  photo: ProjectSurveyPhotoView;
  slotLabel: string;
  /** What the survey records now for a field, in words ("" when nothing), shown beside a proposal. */
  describeCurrent: (fieldKey: string) => string;
  /** Whether a proposal for this field can be saved (the field exists on this survey and takes the value). */
  canAccept: (p: ProjectSurveyPlateProposal) => boolean;
  /** Save one proposal as the office's confirmed value. Resolves to a problem in words, or null when saved. */
  onAccept: (p: ProjectSurveyPlateProposal) => Promise<string | null>;
  /** The photo was removed. `driveNote` is set when its Drive file could not be deleted too (it is still in the project's Survey folder). */
  onDeleted: (driveNote?: string) => void;
}) {
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState<ProjectSurveyPlateRead | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Session FR4 (PJ2F left item): the office can set or clear a photo's caption here, through the existing PATCH route (an empty caption
  // clears it). The caption shown is kept locally once saved, because the panel does not reload the survey for a caption.
  const [caption, setCaption] = useState<string | null>(photo.caption);
  useEffect(() => setCaption(photo.caption), [photo.caption]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingCaption, setSavingCaption] = useState(false);
  const [captionError, setCaptionError] = useState<string | null>(null);

  async function saveCaption() {
    setSavingCaption(true);
    setCaptionError(null);
    try {
      const out = await api.projectSurveys.setPhotoCaption(surveyId, photo.id, draft.trim());
      setCaption(out.photo.caption);
      setEditing(false);
    } catch (e) {
      setCaptionError(friendlyApiError(e, "Could not save the caption."));
    } finally {
      setSavingCaption(false);
    }
  }

  async function readPlate() {
    setReading(true);
    setReadError(null);
    setRead(null);
    try {
      setRead(await api.projectSurveysOffice.readPlate(surveyId, photo.id));
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) setReadError("Reading plates is not set up.");
      else setReadError(friendlyApiError(e, "Could not read this plate. You can type the values in yourself."));
    } finally {
      setReading(false);
    }
  }

  async function accept(p: ProjectSurveyPlateProposal) {
    setAcceptError(null);
    const err = await onAccept(p);
    if (err) setAcceptError(err);
    else setAccepted((s) => new Set(s).add(p.fieldKey));
  }

  async function remove() {
    setDeleting(true);
    setDeleteError(null);
    try {
      const out = await api.projectSurveysOffice.deletePhoto(surveyId, photo.id);
      onDeleted(out.driveFileRemoved ? undefined : out.message);
    } catch (e) {
      setDeleteError(friendlyApiError(e, "Could not delete the photo."));
    } finally {
      setDeleting(false);
    }
  }

  const src = api.projectSurveys.photoUrl(surveyId, photo.id);
  return (
    <li className="w-full max-w-[260px] rounded-ele border border-ele-card-border p-2" data-photo={photo.id}>
      <a href={src} target="_blank" rel="noreferrer" aria-label={`Open the photo: ${slotLabel}`}>
        <img src={src} alt={slotLabel} loading="lazy" className="h-28 w-full rounded-ele object-cover" />
      </a>
      <p className="mt-1 text-xs text-ele-muted">{photo.capturedAt ? formatDateTime(photo.capturedAt) : formatDateTime(photo.uploadedAt)}</p>
      {caption && !editing ? <p className="break-words text-xs text-ele-body" data-caption>{caption}</p> : null}
      {editing ? (
        <div className="mt-1 space-y-1">
          <input
            type="text"
            value={draft}
            maxLength={300}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Photo caption"
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-xs text-ele-strong outline-none focus:border-ele-green max-sm:min-h-11"
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className={quietBtn} onClick={() => void saveCaption()} disabled={savingCaption}>
              {savingCaption ? "Saving..." : "Save caption"}
            </button>
            <button
              type="button"
              className={quietBtn}
              onClick={() => {
                setCaptionError(null);
                setEditing(false);
              }}
              disabled={savingCaption}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {captionError ? (
        <p role="alert" className="mt-1 text-xs text-ele-danger">
          {captionError}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!editing ? (
          <button
            type="button"
            className={quietBtn}
            onClick={() => {
              setDraft(caption ?? "");
              setCaptionError(null);
              setEditing(true);
            }}
          >
            {caption ? "Edit caption" : "Add caption"}
          </button>
        ) : null}
        {isPlateSlot(photo.slotKey) ? (
          <button type="button" className={quietBtn} onClick={() => void readPlate()} disabled={reading}>
            {reading ? "Reading..." : "Read this plate"}
          </button>
        ) : null}
        <ConfirmButton onConfirm={() => void remove()} label="Delete photo" busy={deleting} className={quietBtn} />
      </div>
      {deleteError ? (
        <p role="alert" className="mt-1 text-xs text-ele-danger">
          {deleteError}
        </p>
      ) : null}
      {readError ? (
        <p role="alert" className="mt-2 text-xs text-ele-body">
          {readError}
        </p>
      ) : null}
      {read ? (
        <div className="mt-2 space-y-2" data-plate-read>
          {read.proposals.length === 0 ? (
            <p className="text-xs text-ele-body">{read.message ?? "Nothing could be read from this plate. You can type the values in yourself."}</p>
          ) : (
            <>
              <p className="text-xs text-ele-muted">Read from the photo. Check each against the plate, then accept it.</p>
              <ul className="space-y-2">
                {read.proposals.map((p) => {
                  const current = describeCurrent(p.fieldKey);
                  const done = accepted.has(p.fieldKey);
                  return (
                    <li key={p.fieldKey} className="rounded-ele border border-ele-card-border p-2">
                      <p className="text-xs text-ele-muted">{p.label}</p>
                      <p className="break-words text-sm text-ele-strong">
                        {String(p.value)}
                        {p.unit ? ` ${p.unit}` : ""}
                      </p>
                      <p className="break-words text-xs text-ele-muted">Plate reads: {p.readText}</p>
                      <p className="text-xs text-ele-muted">Recorded now: {current || "nothing"}</p>
                      {done ? (
                        <p className="mt-1 text-xs text-ele-green">Saved, confirmed by the office.</p>
                      ) : canAccept(p) ? (
                        <button type="button" className={`${quietBtn} mt-1`} onClick={() => void accept(p)} aria-label={`Accept ${p.label}`}>
                          Accept
                        </button>
                      ) : (
                        <p className="mt-1 text-xs text-ele-muted">This survey has no place for this value. Type it in yourself if you need it.</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {acceptError ? (
            <p role="alert" className="text-xs text-ele-danger">
              {acceptError}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
